import React, {useEffect} from 'react'
import {Box, Divider, IconButton, Paper} from '@mui/material'
import {EditorContent, useEditor} from '@tiptap/react'
import {StarterKit} from '@tiptap/starter-kit'
import {Underline} from '@tiptap/extension-underline'
import {Link} from '@tiptap/extension-link'
import FormatBoldRoundedIcon from '@mui/icons-material/FormatBoldRounded'
import FormatItalicRoundedIcon from '@mui/icons-material/FormatItalicRounded'
import FormatUnderlinedRoundedIcon from '@mui/icons-material/FormatUnderlinedRounded'
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded'
import FormatListNumberedRoundedIcon from '@mui/icons-material/FormatListNumberedRounded'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import TitleRoundedIcon from '@mui/icons-material/TitleRounded'
import {Controller, useFormContext} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {styled} from 'common'
import {Colors} from 'appearance'
import {useIsRtl} from 'store/Store'

const TOOLBAR_ACTIONS = [
    {
        key: 'bold',
        Icon: FormatBoldRoundedIcon,
        run: (chain) => chain.toggleBold(),
        isActive: (editor) => editor.isActive('bold'),
    },
    {
        key: 'italic',
        Icon: FormatItalicRoundedIcon,
        run: (chain) => chain.toggleItalic(),
        isActive: (editor) => editor.isActive('italic'),
    },
    {
        key: 'underline',
        Icon: FormatUnderlinedRoundedIcon,
        run: (chain) => chain.toggleUnderline(),
        isActive: (editor) => editor.isActive('underline'),
    },
    {
        key: 'heading',
        Icon: TitleRoundedIcon,
        run: (chain) => chain.toggleHeading({level: 2}),
        isActive: (editor) => editor.isActive('heading', {level: 2}),
    },
    {
        key: 'bulletList',
        Icon: FormatListBulletedRoundedIcon,
        run: (chain) => chain.toggleBulletList(),
        isActive: (editor) => editor.isActive('bulletList'),
    },
    {
        key: 'orderedList',
        Icon: FormatListNumberedRoundedIcon,
        run: (chain) => chain.toggleOrderedList(),
        isActive: (editor) => editor.isActive('orderedList'),
    },
]

const Toolbar = ({editor}) => {
    if (!editor) return null

    const setLink = () => {
        const previousUrl = editor.getAttributes('link').href
        // eslint-disable-next-line no-alert
        const url = window.prompt('URL', previousUrl || 'https://')
        if (url === null) return
        if (!url) {
            editor.chain().focus().extendMarkRange('link').unsetLink().run()
            return
        }
        editor.chain().focus().extendMarkRange('link').setLink({href: url}).run()
    }

    return (
        <ToolbarContainer>
            {TOOLBAR_ACTIONS.map(({key, Icon, run, isActive}) => (
                <IconButton
                    key={key}
                    size="small"
                    color={isActive(editor) ? 'primary' : 'default'}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => run(editor.chain().focus()).run()}>
                    <Icon fontSize="small" />
                </IconButton>
            ))}
            <IconButton
                size="small"
                color={editor.isActive('link') ? 'primary' : 'default'}
                onMouseDown={(e) => e.preventDefault()}
                onClick={setLink}>
                <LinkRoundedIcon fontSize="small" />
            </IconButton>
        </ToolbarContainer>
    )
}

const RichTextInput = ({value, onChange, error, minHeight}) => {
    const isRtl = useIsRtl()
    const editor = useEditor({
        extensions: [StarterKit, Underline, Link.configure({openOnClick: false})],
        content: value || '',
        onUpdate: ({editor}) => onChange(editor.getHTML()),
    })

    // Keep the editor in sync when the value is reset from outside (e.g. form reset after send)
    useEffect(() => {
        if (!editor) return
        const isEmptyIncoming = !value
        const isEditorEmpty = editor.isEmpty
        if (
            value !== editor.getHTML() &&
            (isEmptyIncoming ? !isEditorEmpty : true) &&
            !editor.isFocused
        ) {
            editor.commands.setContent(value || '', false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, editor])

    return (
        <EditorWrapper $error={!!error} elevation={0}>
            <Toolbar editor={editor} />
            <Divider />
            <EditorBody $minHeight={minHeight} dir={isRtl ? 'rtl' : 'ltr'}>
                <EditorContent editor={editor} />
            </EditorBody>
        </EditorWrapper>
    )
}

const FormRichText = ({name, label, i18nCategory, defaultValue, error, minHeight}) => {
    const {control} = useFormContext()
    const {t} = useTranslation()

    return (
        <Box width="100%">
            {label && <Label>{t(`${i18nCategory}.${label}`)}</Label>}
            <Controller
                name={name}
                control={control}
                render={({field: {onChange}}) => (
                    <RichTextInput
                        value={defaultValue}
                        onChange={onChange}
                        error={error}
                        minHeight={minHeight}
                    />
                )}
            />
            {error?.message && <ErrorText>{t(`error.${error?.message}`)}</ErrorText>}
        </Box>
    )
}

export default FormRichText

const Label = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: ${Colors.gray};
    margin-bottom: 6px;
`

const ErrorText = styled.div`
    font-size: 12px;
    color: ${Colors.danger};
    margin-top: 4px;
`

const ToolbarContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 2px;
    padding: 6px 8px;
`

const EditorWrapper = styled(Paper)`
    border-radius: 8px !important;
    box-shadow: none !important;
    border: 1px solid ${({$error}) => ($error ? Colors.ERROR_BORDER : Colors.LIGHT_GRAY)} !important;
    overflow: hidden;
`

const EditorBody = styled.div`
    padding: 12px 14px;
    max-height: 360px;
    overflow-y: auto;
    min-height: ${({$minHeight}) => $minHeight || 180}px;
    cursor: text;

    .ProseMirror {
        outline: none;
        min-height: ${({$minHeight}) => $minHeight || 180}px;
    }

    .ProseMirror p {
        margin: 0 0 10px;
    }

    .ProseMirror h2 {
        font-size: 20px;
        font-weight: 600;
        margin: 0 0 10px;
    }

    .ProseMirror ul,
    .ProseMirror ol {
        margin: 0 0 10px;
        padding: '${({dir}) => (dir === 'rtl' ? 'right' : 'left')}: 22px';
    }

    .ProseMirror a {
        color: ${Colors.primary};
    }
`
